import {
  ComputedFieldProps,
  LocalizedString,
  useComponentsWrapperContext,
} from "@dbn/omnichannel-design-system-g4p";
import { option } from "fp-ts";
import { Option } from "fp-ts/Option";
import { useFormatIncomeCurrency, useFormatMessage } from "../../../intl";
import { constant, pipe } from "fp-ts/function";
import { ReadOnlyFieldToValue } from "../../../Common/ReadOnlyField/ReadOnlyFieldToValue";
import { NumberField } from "../../../Common/NumberField/NumberField";
import { useTooltipAccessibility } from "../../../Common/Tooltip/Tooltip";

type MonthlyIncomeProps = {
  fieldProps: ComputedFieldProps<Option<number>>;
  currency?: string;
  description?: LocalizedString;
  descriptionAriaLabel?: LocalizedString;
  tooltipCloseIconAriaLabel?: LocalizedString;
  label?: LocalizedString;
  placeholder?: LocalizedString;
  min?: number;
  max?: number;
};

export function MonthlyIncome(props: MonthlyIncomeProps) {
  const formatMessage = useFormatMessage();
  const formatCurrency = useFormatIncomeCurrency();
  const { componentType } = useComponentsWrapperContext();
  const tooltipAccessibility = useTooltipAccessibility(
    componentType,
    props.description,
    props.descriptionAriaLabel,
    props.tooltipCloseIconAriaLabel
  );

  return props.fieldProps.disabled ? (
    <ReadOnlyFieldToValue
      value={pipe(
        props.fieldProps.value,
        option.map(value => String(value)),
        option.getOrElse(constant(""))
      )}
      label={
        !!props.label
          ? props.label
          : formatMessage("StandardLoan.EmployedCard.monthlyIncome")
      }
      description={props.description}
      {...tooltipAccessibility}
    />
  ) : (
    <NumberField
      {...props.fieldProps}
      rightContent={pipe(
        props.currency,
        option.fromNullable,
        option.map(formatCurrency)
      )}
      label={
        !!props.label
          ? props.label
          : formatMessage("StandardLoan.EmployedCard.monthlyIncome")
      }
      placeholder={
        !!props.placeholder
          ? props.placeholder
          : formatMessage("StandardLoan.EmployedCard.monthlyIncomePlaceholder")
      }
      description={props.description}
      {...tooltipAccessibility}
      min={props.min}
      max={props.max}
      formatOptions={{ maximumFractionDigits: 3 }}
      locale="sk"
    />
  );
}
